import { Formik, FormikProps } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ProductCreate } from '../../Inventory/Product';
import { EstimateAndQuoteCreateCustomerSelectEdit } from '../EstimateAndQuote/EstimateAndQuoteCreateCustomerSelectEdit';

import { Box, BoxColumn, BoxRowColumn } from '@components/Box';
import { CollapsibleBoxColumn } from '@components/CollapsibleBoxColumn';
import { FormSectionContainer } from '@components/Container';
import { CustomerDisplayCard } from '@components/CustomerDisplayCard';
import { ExpandingBoxColumn } from '@components/ExpandingBoxColumn';
import {
  FormikDateInput,
  FormikSelect,
  FormikTextArea,
} from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { FormActionsBar } from '@components/FormActionsBar';
import { LineItemsTable } from '@components/LineItemsTable';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { routes } from '@config/routes';
import {
  useNotification,
  useSalesTaxEffectiveRates,
  useCustomerData,
  useProductData,
  useFormSubmission,
  useTaxCalculation,
  useLineItems,
} from '@hooks/index';
import { CustomerViewModel } from '@models/Customer';
import {
  RecurringInvoiceViewModel,
  recurringInvoiceDefaultValue,
  recurringInvoiceValidation,
  paymentDueDateSelectOptions,
  repeatTypeSelectOptions,
  weekdayScheduleSelectionOptions,
  monthdayScheduleSelectOptions,
  monthScheduleSelectOptions,
  recurrenceEndTypeSelectOptions,
  customIntervalTypeSelectOptions,
  timeZoneSelectOptions,
} from '@models/RecurringInvoice';
import {
  RecurringInvoiceDetailViewModel,
  recurringInvoiceDetailDefaultValue,
} from '@models/RecurringInvoiceDetail';
import {
  recurringInvoiceDetailSalesTaxDefaultValue,
  RecurringInvoiceDetailSalesTaxViewModel,
} from '@models/RecurringInvoiceDetailSalesTax';
import { RecurringInvoiceService } from '@services/RecurringInvoice';

export const RecurringInvoiceEdit = () => {
  const { showInfo, showSuccess, showError } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocation =
    location.state?.from || routes.RNC_RECURRING_INVOICES;

  // ===== SHARED HOOKS =====
  const {
    selectedCustomer,
    customerContacts,
    customerBillingAddresses,
    customerShippingAddresses,
    loadSelectedCustomer,
    loadSelectedContact,
    initializeCustomerData,
  } = useCustomerData({
    onError: showError,
    onCustomerLoaded: (customer) => {
      recalculateTaxAmountsForCustomer(customer);
    },
  });

  const {
    products,
    loadProducts,
    getProductById,
    refreshProducts: refreshProductsList,
  } = useProductData({
    onError: showError,
  });

  const {
    salesTaxes,
    salesTaxEffectiveRates,
    loadData: loadSalesTaxEffectiveRates,
  } = useSalesTaxEffectiveRates();

  const {
    setFormikPropsRef,
    getIsTaxExempt,
    handleCustomerTaxExemptChange,
    setTaxExemptStatus,
  } = useTaxCalculation();

  const [showCreateProduct, setShowCreateProduct] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [recurringInvoice, setRecurringInvoice] =
    useState<RecurringInvoiceViewModel>(recurringInvoiceDefaultValue);

  // Line items hook
  const {
    lineItems: recurringInvoiceDetails,
    setLineItems: setRecurringInvoiceDetails,
    addLineItem: handleAddRecurringInvoiceDetail,
    removeLineItem: handleRemoveRecurringInvoiceDetail,
    updateLineItemQuantity,
    updateLineItemSalesTax,
    updateSalesTaxRates,
    recalculateTaxAmounts,
    getSalesTaxesBySalesTaxID,
    updateLineItemFromProduct,
  } = useLineItems<
    RecurringInvoiceDetailViewModel,
    RecurringInvoiceDetailSalesTaxViewModel
  >(
    {
      defaultLineItem: recurringInvoiceDetailDefaultValue,
      defaultSalesTax: recurringInvoiceDetailSalesTaxDefaultValue,
      salesTaxesKey: 'recurringInvoiceDetailSalesTaxes',
    },
    { onInfo: showInfo }
  );

  const { setSubmitType, handlePostSubmitNavigation, getSaveMode } =
    useFormSubmission({
      listRoute: routes.RNC_RECURRING_INVOICES,
      getEditRoute: (id) => `${routes.RNC_RECURRING_INVOICES}/${id}/edit`,
    });

  const [repeatType, setRepeatType] = useState<string | null>(null);
  const [customIntervalType, setCustomIntervalType] = useState<string | null>(
    null
  );
  const [recurrenceEndType, setRecurrenceEndType] = useState<string | null>(
    '1'
  );
  const [isLeftColumnCollapsed, setIsLeftColumnCollapsed] = useState(false);

  const formikRef = useRef<FormikProps<RecurringInvoiceViewModel>>(null);

  const loadRecurringInvoice = useCallback(async () => {
    const result = await RecurringInvoiceService.getById(Number(id));
    if (result.isSuccess && 'data' in result) {
      setRecurringInvoice(result.data);

      // Initialize customer data, but don't trigger a formik update yet
      if (result.data.customerID) {
        await initializeCustomerData(result.data.customerID);
      }
      setTaxExemptStatus(result.data.isTaxExempt);

      // Set line items
      if (
        result.data.recurringInvoiceDetails &&
        result.data.recurringInvoiceDetails.length > 0
      ) {
        setRecurringInvoiceDetails(result.data.recurringInvoiceDetails);
        // Update tax rates with effective rates after setting line items
        // Use setTimeout to ensure state has been updated
        setTimeout(() => {
          updateSalesTaxRates(salesTaxEffectiveRates, result.data.isTaxExempt);
        }, 0);
      }
    } else {
      const errorMessage = `Failed to load recurring invoice: ${result.errorMessages}`;
      console.error(errorMessage);
      showError(errorMessage);
    }
  }, [id]);

  const recalculateTaxAmountsForCustomer = (
    customer: CustomerViewModel | null
  ) => {
    const isTaxExempt = handleCustomerTaxExemptChange(customer);
    recalculateTaxAmounts(isTaxExempt);
  };

  const loadSelectedProduct = async (productId: string, index: number) => {
    const product = await getProductById(Number(productId));
    if (product) {
      const isTaxExempt = getIsTaxExempt();

      const productSalesTaxes = product.productSalesTaxes.map((tax) => {
        const effectiveRate =
          salesTaxEffectiveRates.find((rate) => rate.id === tax.salesTaxId)
            ?.rate ?? 0;
        return {
          salesTaxID: tax.salesTaxId,
          salesTaxAbbreviation: tax.salesTax.abbreviation,
          rate: effectiveRate,
        } as RecurringInvoiceDetailSalesTaxViewModel;
      });

      updateLineItemFromProduct(
        index,
        {
          productID: product.id,
          description: product.description,
          price: product.price,
          quantity: 1,
          productSalesTaxes: productSalesTaxes,
        },
        isTaxExempt
      );
    }
  };

  const handleUpdateRecurringInvoiceDetailQuantity = (
    index: number,
    quantity: number
  ) => {
    const isTaxExempt = getIsTaxExempt();
    updateLineItemQuantity(index, quantity, isTaxExempt);
  };

  const handleUpdateRecurringInvoiceDetailSalesTax = (
    invoiceDetailIndex: number,
    invoiceDetailSalesTaxIndex: number,
    _invoiceDetailSalesTaxesLength: number,
    salesTaxID: number
  ) => {
    const salesTax = salesTaxEffectiveRates.find((o) => o.id == salesTaxID);
    updateLineItemSalesTax(
      invoiceDetailIndex,
      invoiceDetailSalesTaxIndex,
      salesTax,
      getIsTaxExempt()
    );
  };

  useEffect(() => {
    setIsLoading(true);
    const loadInitialData = async () => {
      // Load products and tax rates first
      await Promise.all([
        loadProducts(),
        loadSalesTaxEffectiveRates(new Date()),
      ]);
      // Then load the recurring invoice which will populate line items
      await loadRecurringInvoice();
      setIsLoading(false);
    };
    loadInitialData();
    // Only load once on mount
    // eslint-disable-next-line
  }, []);

  if (isLoading) {
    return (
      <>
        <Loading />
      </>
    );
  } else {
    return (
      <>
        <Box>
          <div className="pt-2 lg:pb-6 md:pb-4 pb-2">
            <p className="w-full text-center lg:text-2xl md:text-lg text-md font-bold text-primary-900">
              Edit Recurring Invoice
            </p>
          </div>
          <Formik
            innerRef={formikRef}
            initialValues={recurringInvoice}
            validationSchema={recurringInvoiceValidation}
            onSubmit={async (values, { setSubmitting }) => {
              const formattedValues = {
                ...values,
                recurringInvoiceDetails: recurringInvoiceDetails.filter(
                  (d) => d.productID > 0
                ),
                saveMode: getSaveMode(),
              };

              const result = await RecurringInvoiceService.update(
                Number(id),
                formattedValues
              );

              if (result.isSuccess) {
                showSuccess('Recurring invoice updated successfully.');
                handlePostSubmitNavigation({
                  success: true,
                  id: Number(id),
                });
              } else {
                showError(
                  `Failed to update recurring invoice. ${result.errorMessages}`
                );
              }

              setSubmitting(false);
            }}
            enableReinitialize
          >
            {(formikProps) => {
              const { values, setFieldValue } = formikProps;
              setFormikPropsRef(formikProps);

              const handleRepeatTypeChange = (
                e: React.ChangeEvent<HTMLSelectElement>
              ) => {
                const selectedValue = e.target.value;
                formikProps.setFieldValue('repeatType', selectedValue);
                setRepeatType(selectedValue);
              };

              const handleCustomIntervalTypeChange = (
                e: React.ChangeEvent<HTMLSelectElement>
              ) => {
                const selectedValue = e.target.value;
                formikProps.setFieldValue('customIntervalType', selectedValue);
                setCustomIntervalType(selectedValue);
              };

              const handleRecurrenceEndTypeChange = (
                e: React.ChangeEvent<HTMLSelectElement>
              ) => {
                const selectedValue = e.target.value;
                formikProps.setFieldValue('recurrenceEndType', selectedValue);
                setRecurrenceEndType(selectedValue);
              };

              return (
                <>
                  <form method="POST" onSubmit={formikProps.handleSubmit}>
                    <FormSectionContainer>
                      {/* Calling Card - Only visible when left column is collapsed */}
                      {isLeftColumnCollapsed && selectedCustomer && (
                        <CustomerDisplayCard
                          selectedCustomer={selectedCustomer}
                          contactID={values.contactID}
                          contactEmail={values.contactEmail}
                          customerContacts={customerContacts}
                          onEditClick={() => setIsLeftColumnCollapsed(false)}
                        />
                      )}
                      <BoxRowColumn>
                        <CollapsibleBoxColumn
                          isCollapsed={isLeftColumnCollapsed}
                          onToggleCollapse={() =>
                            setIsLeftColumnCollapsed(!isLeftColumnCollapsed)
                          }
                          canCollapse={!!selectedCustomer}
                          collapseTitle="Collapse form"
                          disabledTitle="Please select a customer first"
                        >
                          <EstimateAndQuoteCreateCustomerSelectEdit
                            initialSelectedCustomerId={`${recurringInvoice.customerID}`}
                            onCustomerSelectChange={(customerId: string) => {
                              loadSelectedCustomer(customerId, formikProps);
                            }}
                          />

                          {selectedCustomer && (
                            <div className="mb-4">
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={values.isTaxExempt || false}
                                  onChange={(e) => {
                                    setFieldValue(
                                      'isTaxExempt',
                                      e.target.checked
                                    );
                                    console.log(
                                      'Manual tax exemption toggle:',
                                      e.target.checked
                                    );
                                    // Recalculate tax amounts when tax exemption status changes
                                    setTimeout(() => {
                                      recalculateTaxAmountsForCustomer(
                                        selectedCustomer
                                      );
                                    }, 0);
                                  }}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                  Tax Exempt
                                </span>
                              </label>
                            </div>
                          )}

                          <FormikSelect
                            label="Contact Name"
                            name="contactID"
                            selection={customerContacts}
                            onChange={(
                              event: React.ChangeEvent<HTMLSelectElement>
                            ) => {
                              loadSelectedContact(
                                event.target.value,
                                formikProps
                              );
                            }}
                          />

                          <BoxRowColumn>
                            <BoxColumn>
                              <FormikInput
                                label="Email"
                                name="contactEmail"
                                readonly={true}
                              />
                            </BoxColumn>

                            <BoxColumn>
                              <FormikInput
                                label="Phone"
                                name="contactPhone"
                                readonly={true}
                              />
                            </BoxColumn>
                          </BoxRowColumn>
                          <BoxColumn>
                            <FormikSelect
                              label="Billing Address"
                              name="billingAddressID"
                              selection={customerBillingAddresses}
                            />

                            {!values.isSameShipping ? (
                              <FormikSelect
                                label="Shipping Address"
                                name="shippingAddressID"
                                selection={customerShippingAddresses}
                              />
                            ) : (
                              <>
                                <label className="block lg:text-sm md:text-sm text-xs font-medium text-primary-700">
                                  Shipping Address
                                </label>
                                <i>Same as Billing Address</i>
                              </>
                            )}
                          </BoxColumn>

                          <BoxRowColumn>
                            <BoxColumn>
                              <FormikInput
                                label="Invoice Number"
                                name="recurringInvoiceNumber"
                                readonly={true}
                              />
                            </BoxColumn>

                            <BoxColumn>
                              <FormikInput
                                label="PO/SO Number"
                                name="poNumber"
                              />
                            </BoxColumn>
                          </BoxRowColumn>

                          <BoxRowColumn>
                            <BoxColumn>
                              <FormikDateInput
                                label="Invoice Date"
                                name="date"
                                type="date"
                                readonly={true}
                              />
                            </BoxColumn>

                            <BoxColumn>
                              <FormikSelect
                                label="Payment Due"
                                name="paymentDueDate"
                                selection={paymentDueDateSelectOptions}
                              />
                            </BoxColumn>
                          </BoxRowColumn>

                          <label className="block lg:text-sm md:text-sm text-xs font-medium text-primary-700">
                            Set Schedule
                          </label>
                          <BoxRowColumn>
                            <BoxColumn>
                              <FormikSelect
                                label="Repeat this Invoice"
                                name="repeatType"
                                selection={repeatTypeSelectOptions}
                                onChange={handleRepeatTypeChange}
                                value={values.repeatType}
                              />
                            </BoxColumn>

                            {repeatType === '5' && (
                              <BoxColumn>
                                <FormikInput
                                  label="Every"
                                  name="customRepeatInterval"
                                />
                              </BoxColumn>
                            )}

                            {repeatType === '2' && (
                              <BoxColumn>
                                <FormikSelect
                                  label="Every"
                                  name="weekdaySchedule"
                                  selection={weekdayScheduleSelectionOptions}
                                />
                              </BoxColumn>
                            )}

                            {repeatType === '3' && (
                              <BoxColumn>
                                <FormikSelect
                                  label="On the"
                                  name="monthdaySchedule"
                                  selection={monthdayScheduleSelectOptions}
                                />
                                of every month.
                              </BoxColumn>
                            )}
                          </BoxRowColumn>

                          {repeatType === '4' && (
                            <BoxRowColumn>
                              <BoxColumn>
                                <FormikSelect
                                  label="Every"
                                  name="monthSchedule"
                                  selection={monthScheduleSelectOptions}
                                />
                              </BoxColumn>
                              <BoxColumn>
                                <FormikSelect
                                  label="On the"
                                  name="monthdaySchedule"
                                  selection={monthdayScheduleSelectOptions}
                                />
                                day of the month.
                              </BoxColumn>
                            </BoxRowColumn>
                          )}

                          {repeatType === '5' && (
                            <>
                              <BoxRowColumn>
                                <BoxColumn></BoxColumn>
                                <BoxColumn>
                                  <FormikSelect
                                    label=""
                                    name="customIntervalType"
                                    selection={customIntervalTypeSelectOptions}
                                    onChange={handleCustomIntervalTypeChange}
                                  />
                                </BoxColumn>
                              </BoxRowColumn>

                              {customIntervalType === '2' && ( //Week
                                <>
                                  <BoxRowColumn>
                                    <BoxColumn></BoxColumn>
                                    <BoxColumn>
                                      <FormikSelect
                                        label="on"
                                        name="weekdaySchedule"
                                        selection={
                                          weekdayScheduleSelectionOptions
                                        }
                                      />
                                    </BoxColumn>
                                  </BoxRowColumn>
                                </>
                              )}

                              {customIntervalType === '3' && ( //Month
                                <>
                                  <BoxRowColumn>
                                    <BoxColumn></BoxColumn>
                                    <BoxColumn>
                                      <FormikSelect
                                        label="on the"
                                        name="monthdaySchedule"
                                        selection={
                                          monthdayScheduleSelectOptions
                                        }
                                      />
                                      day of the month
                                    </BoxColumn>
                                  </BoxRowColumn>
                                </>
                              )}

                              {customIntervalType === '4' && ( //year
                                <>
                                  <BoxRowColumn>
                                    <BoxColumn>
                                      <FormikSelect
                                        label="in"
                                        name="monthSchedule"
                                        selection={monthScheduleSelectOptions}
                                      />
                                    </BoxColumn>
                                    <BoxColumn>
                                      <FormikSelect
                                        label="on the"
                                        name="monthdaySchedule"
                                        selection={
                                          monthdayScheduleSelectOptions
                                        }
                                      />
                                      day of the month
                                    </BoxColumn>
                                  </BoxRowColumn>
                                </>
                              )}
                            </>
                          )}

                          <BoxRowColumn>
                            <BoxColumn>
                              <FormikDateInput
                                label="Create first invoice on"
                                name="firstInvoiceDate"
                              />
                            </BoxColumn>
                            <BoxColumn>
                              <FormikSelect
                                label="And end"
                                name="recurrenceEndType"
                                selection={recurrenceEndTypeSelectOptions}
                                onChange={handleRecurrenceEndTypeChange}
                              />
                            </BoxColumn>
                          </BoxRowColumn>
                          {recurrenceEndType === '1' && (
                            <>
                              <BoxRowColumn>
                                <BoxColumn></BoxColumn>
                                <BoxColumn>
                                  <FormikInput
                                    label=""
                                    name="recurrenceEndAfter"
                                  />
                                  invoices
                                </BoxColumn>
                              </BoxRowColumn>
                            </>
                          )}

                          {recurrenceEndType === '2' && (
                            <>
                              <BoxRowColumn>
                                <BoxColumn></BoxColumn>
                                <BoxColumn>
                                  <FormikDateInput
                                    label=""
                                    name="recurrenceEndOn"
                                  />
                                </BoxColumn>
                              </BoxRowColumn>
                            </>
                          )}

                          <FormikSelect
                            label="Timezone"
                            name="timeZone"
                            selection={timeZoneSelectOptions}
                          />
                          <label className="block text-sm font-medium text-primary-700 py-0">
                            Set a time zone to ensure invoice delivery in the
                            morning based on the recipient{`'`}s time zone.
                          </label>

                          <FormikTextArea label="Notes" name="notes" />
                        </CollapsibleBoxColumn>

                        <ExpandingBoxColumn isExpanded={isLeftColumnCollapsed}>
                          <div className="py-4">
                            <LineItemsTable
                              lineItems={recurringInvoiceDetails}
                              products={products}
                              salesTaxes={salesTaxes}
                              salesTaxesKey="recurringInvoiceDetailSalesTaxes"
                              onProductSelect={loadSelectedProduct}
                              onQuantityChange={
                                handleUpdateRecurringInvoiceDetailQuantity
                              }
                              onSalesTaxChange={
                                handleUpdateRecurringInvoiceDetailSalesTax
                              }
                              onAddLineItem={handleAddRecurringInvoiceDetail}
                              onRemoveLineItem={
                                handleRemoveRecurringInvoiceDetail
                              }
                              onCreateProductClick={() =>
                                setShowCreateProduct(true)
                              }
                              getSalesTaxesBySalesTaxID={() =>
                                getSalesTaxesBySalesTaxID
                              }
                              formikValues={values}
                            />
                          </div>

                          <div className="flex items-end justify-end h-full">
                            <FormActionsBar
                              onCancel={() => navigate(previousLocation)}
                              isSubmitting={formikProps.isSubmitting}
                              isValid={formikProps.isValid}
                              buttons={[
                                {
                                  label: 'Save & Close',
                                  submitType: 'saveAndClose',
                                },
                                { label: 'Save', submitType: 'save' },
                              ]}
                              onSubmitTypeChange={setSubmitType}
                            />
                          </div>
                        </ExpandingBoxColumn>
                      </BoxRowColumn>
                    </FormSectionContainer>
                  </form>
                </>
              );
            }}
          </Formik>
        </Box>

        {showCreateProduct && (
          <>
            <Modal show={showCreateProduct}>
              <ProductCreate
                onClose={() => {
                  setShowCreateProduct(false);
                  refreshProductsList();
                }}
              />
            </Modal>
          </>
        )}
      </>
    );
  }
};
